import type { NudgeRecommendationDetails } from '@atlassian/convo-ai-api';
import { type ConversationAction } from '@atlassian/rovo-conversation-actions/types';

import type { MigratedBaseConversationAction } from '../types';
import { enrichNudge } from './enrichNudge';

/**
 * Finds the first registered nudge recommended by a service response. When multiple
 * nudges are present, the service response (BE) determines order.
 *
 * @param recommendations Nudges recommended by a service response (BE)
 * @param availableNudges Nudges registered by the consumer (FE)
 * @returns ConversationAction, or undefined if no match found
 */
export const findActiveNudge = (
	recommendations: NudgeRecommendationDetails[],
	availableNudges: ConversationAction[],
): MigratedBaseConversationAction | undefined => {
	for (const recommendation of recommendations) {
		// Primary match: nudge registered with id matching the BE feature_id
		const matchedNudge = availableNudges.find(
			(nudge) => nudge.id === recommendation.feature_details.feature_id,
		);

		// Fallback match: nudge registered with nudgeContext.id matching the BE feature_id.
		// This exists because some nudge types use nudgeContext.id instead of id for matching —
		// a data model inconsistency that should be unified.
		const matchedNudgeByContextId = availableNudges.find(
			(action) => action.nudgeContext?.id === recommendation.feature_details.feature_id,
		);

		if (matchedNudge) {
			const enriched = enrichNudge(
				{ ...matchedNudge, recommendationNudgeId: recommendation.nudge_id },
				recommendation,
			);
			if (enriched) return enriched;
		}

		if (matchedNudgeByContextId) {
			const enriched = enrichNudge(
				{ ...matchedNudgeByContextId, recommendationNudgeId: recommendation.nudge_id },
				recommendation,
			);
			if (enriched) return enriched;
		}
	}

	return undefined;
};
